import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentIcon, BellIcon } from '@heroicons/react/24/solid';
import { Crown } from 'lucide-react';

const PushNotificationSetup = () => {
  const [storeUrl, setStoreUrl] = useState('');
  const [platform, setPlatform] = useState('woocommerce');
  const [vapidKeys, setVapidKeys] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [integrationCode, setIntegrationCode] = useState({});
  const [stats, setStats] = useState({ subscribers: 0, notificationsSent: 0, clickRate: 0 });
  const [selectedMethod, setSelectedMethod] = useState('method1');
  const [currentBaseUrl, setCurrentBaseUrl] = useState('');
  const [subscription, setSubscription] = useState({ plan: 'free', status: null });
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  useEffect(() => {
    // Get store URL from localStorage or props
    let savedStoreUrl = localStorage.getItem('storeUrl') || '';
    const savedPlatform = localStorage.getItem('platform') || 'woocommerce';
    
    // Normalize store URL (remove trailing slash for consistency with backend)
    if (savedStoreUrl) {
      savedStoreUrl = savedStoreUrl.replace(/\/$/, '');
    }
    
    setStoreUrl(savedStoreUrl);
    setPlatform(savedPlatform);
    
    // Initialize baseUrl
    let baseUrl = process.env.REACT_APP_API_URL;
    if (!baseUrl) {
      const currentOrigin = window.location.origin;
      const currentHost = window.location.hostname;
      const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost.startsWith('192.168.') || currentHost.startsWith('10.') || currentHost.startsWith('172.');
      baseUrl = isLocalhost ? 'http://localhost:3003' : currentOrigin;
    }
    setCurrentBaseUrl(baseUrl);
    
    // If no store URL, try to get from window location
    if (!savedStoreUrl) {
      const currentOrigin = window.location.origin;
      if (currentOrigin && currentOrigin !== 'http://localhost:3000') {
        setStoreUrl(currentOrigin.replace(/\/$/, '')); // Normalize here too
      }
    }
  }, []);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setSubscriptionLoading(true);
        const response = await api.get('/api/subscribe/status');
        const subscriptionData = response.data || { plan: 'free', status: 'active' };
        console.log('📊 Subscription status loaded:', subscriptionData);
        setSubscription(subscriptionData);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        // Default to free plan if subscription check fails - still show the page
        setSubscription({ plan: 'free', status: 'active' });
      } finally {
        setSubscriptionLoading(false);
      }
    };
    
    fetchSubscription();
  }, []);

  const generateIntegrationCode = useCallback((publicKey) => {
    // Get base URL (where CartResQ backend is hosted)
    let baseUrl = process.env.REACT_APP_API_URL;
    
    if (!baseUrl) {
      const currentOrigin = window.location.origin;
      const currentHost = window.location.hostname;
      const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost.startsWith('192.168.') || currentHost.startsWith('10.') || currentHost.startsWith('172.');
      
      if (isLocalhost) {
        baseUrl = 'http://localhost:3003';
      } else {
        // For production, try to detect from current origin
        baseUrl = currentOrigin;
      }
    }
    
    // Store baseUrl in state so it's accessible in render
    setCurrentBaseUrl(baseUrl);
    
    // API URL is baseUrl + /api
    const apiUrl = `${baseUrl}/api`;
    
    // Store URL - use the storeUrl from state or current origin as fallback
    let storeUrlValue = storeUrl || window.location.origin;
    
    // Service worker path - MUST be same-origin (relative path)
    // Service workers cannot be registered from different origins
    // Auto-detect based on store URL path
    let swPath = '/sw.js'; // Default to root
    if (storeUrlValue && storeUrlValue !== window.location.origin) {
      try {
        const storeUrlObj = new URL(storeUrlValue);
        const pathname = storeUrlObj.pathname;
        if (pathname && pathname !== '/') {
          // If store is in a subdirectory, use that path
          swPath = `${pathname.replace(/\/$/, '')}/sw.js`;
        }
      } catch (e) {
        // If URL parsing fails, use default
        console.warn('Could not parse store URL for swPath detection');
      }
    }
    
    // Simple PHP-safe string encoding using addslashes
    const phpEncode = (str) => {
      if (!str) return "''";
      // Escape for PHP single-quoted strings
      return "'" + String(str).replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
    };
    
    // Generate sw.js deployment code - deploy to WordPress root (SAFE VERSION)
    const swDeploymentCode = `// CartResQ Push Notifications - Safe Auto-Deploy Service Worker
// This code is 100% safe and won't break your site even if something goes wrong
if (!function_exists('cartresq_deploy_service_worker')) {
    function cartresq_deploy_service_worker() {
        // Safety check: Only run if WordPress is fully loaded
        if (!defined('ABSPATH') || !function_exists('wp_remote_get')) {
            return; // Silent fail - don't break the site
        }
        
        try {
            $sw_url = ${phpEncode(`${baseUrl}/sw.js`)};
            $sw_path = ABSPATH . 'sw.js';
            
            // Safety: Validate paths
            if (empty($sw_url) || empty($sw_path)) {
                return; // Silent fail
            }
            
            // Safety: Check if directory is writable before attempting
            if (!is_writable(ABSPATH)) {
                // Silent fail - don't log errors that might worry users
                return;
            }
            
            // Safety: Check if file exists and is recent (less than 24 hours old)
            // This prevents unnecessary updates and reduces server load
            if (file_exists($sw_path)) {
                $file_time = @filemtime($sw_path);
                if ($file_time !== false) {
                    $current_time = time();
                    $hours_old = ($current_time - $file_time) / 3600;
                    
                    // If file is less than 24 hours old, skip update (saves resources)
                    if ($hours_old < 24) {
                        return;
                    }
                }
            }
            
            // Safety: Use @ to suppress warnings, wrap in try-catch for errors
            $response = @wp_remote_get($sw_url, array(
                'timeout' => 10, // Reduced timeout to prevent hanging
                'sslverify' => false,
                'headers' => array('Cache-Control' => 'no-cache')
            ));
            
            // Safety: Check for errors before proceeding
            if (is_wp_error($response)) {
                // Silent fail - don't log to avoid cluttering error logs
                return;
            }
            
            $response_code = wp_remote_retrieve_response_code($response);
            if ($response_code !== 200) {
                // Silent fail - file might be temporarily unavailable
                return;
            }
            
            $sw_content = wp_remote_retrieve_body($response);
            
            // Safety: Validate content before writing
            if (empty($sw_content) || strlen($sw_content) < 100) {
                return; // Invalid content - don't write
            }
            
            // Safety: Use @ to suppress warnings, check result
            $result = @file_put_contents($sw_path, $sw_content);
            if ($result !== false) {
                @chmod($sw_path, 0644); // Set safe permissions
                // Success - optional log (commented to reduce log clutter)
                // error_log('CartResQ: Service worker deployed successfully');
            }
            // If write fails, silent fail - don't break the site
            
        } catch (Exception $e) {
            // Catch any unexpected errors - silent fail to prevent site breakage
            return;
        } catch (Error $e) {
            // Catch fatal errors in PHP 7+ - silent fail
            return;
        }
    }
    
    // Safety: Only hook if WordPress actions are available
    if (function_exists('add_action')) {
        // Run on init (frontend) and admin_init (admin) to ensure it deploys
        add_action('init', 'cartresq_deploy_service_worker', 99); // Low priority to run after other plugins
        add_action('admin_init', 'cartresq_deploy_service_worker', 99);
    }
}`;
    
    const method1Code = `// CartResQ Push Notifications - SAFE VERSION (won't break your site)
// Add this code to the bottom of your functions.php file
${swDeploymentCode}

// CartResQ Push Notification Widget - Safe initialization
if (!function_exists('cartresq_push_notifications')) {
    function cartresq_push_notifications() {
        // Safety check: Only run on frontend, not in admin
        if (is_admin()) {
            return;
        }
        
        // Safety: Check if WordPress functions exist
        if (!function_exists('esc_url') || !function_exists('json_encode')) {
            return; // Silent fail
        }
        
        try {
            $baseUrl = ${phpEncode(baseUrl)};
            $apiUrl = ${phpEncode(apiUrl)};
            $storeUrl = ${phpEncode(storeUrlValue)};
            $swPath = ${phpEncode(swPath)};
            
            // Safety: Validate URLs before outputting
            if (empty($baseUrl) || empty($apiUrl)) {
                return; // Silent fail
            }
            ?>
            <script src="<?php echo esc_url($baseUrl); ?>/cartresq-push-widget.js"></script>
            <script>
                // Safe initialization with error handling
                (function() {
                    try {
                        if (typeof CartResQ !== 'undefined') {
                            CartResQ.init({
                                apiUrl: <?php echo json_encode($apiUrl); ?>,
                                storeUrl: <?php echo json_encode($storeUrl); ?>,
                                platform: 'woocommerce',
                                swPath: <?php echo json_encode($swPath); ?>,
                                showPrompt: true,
                                promptDelay: 5000
                            });
                        }
                    } catch (e) {
                        // Silent error - don't break the site
                        console.log('CartResQ: Initialization error (non-critical)');
                    }
                })();
            </script>
            <?php
        } catch (Exception $e) {
            // Catch any errors - silent fail
            return;
        } catch (Error $e) {
            // Catch fatal errors in PHP 7+
            return;
        }
    }
    
    // Safety: Only hook if WordPress is ready
    if (function_exists('add_action') && !is_admin()) {
        add_action('wp_footer', 'cartresq_push_notifications', 99); // Low priority
    }
}

// Optional: Shortcode for manual button (safe version)
if (!function_exists('cartresq_push_button')) {
    function cartresq_push_button($atts) {
        // Safety: Only output on frontend
        if (is_admin()) {
            return '';
        }
        
        try {
            return '<button onclick="if(typeof CartResQ !== \\'undefined\\') CartResQ.subscribe();" class="cartresq-push-button">🔔 Enable Notifications</button>';
        } catch (Exception $e) {
            return ''; // Silent fail
        }
    }
    
    // Safety: Only register shortcode if function exists
    if (function_exists('add_shortcode')) {
        add_shortcode('cartresq_push', 'cartresq_push_button');
    }
}`;

    // Generate standalone PHP file that can be uploaded (no functions.php needed)
    const method2StandaloneFile = `<?php
/**
 * CartResQ Push Notification - Standalone Deployment File
 * 
 * INSTRUCTIONS:
 * 1. Save this file as: cartresq-deploy-sw.php
 * 2. Upload it to your WordPress ROOT directory (where wp-config.php is located)
 * 3. Visit: https://your-site.com/cartresq-deploy-sw.php (one time)
 * 4. This will automatically create sw.js in your WordPress root
 * 5. After sw.js is created, you can delete this file (optional)
 * 
 * NO functions.php ACCESS NEEDED - This is a standalone solution!
 */

// Load WordPress
require_once('wp-load.php');

// Security: Only allow if WordPress is loaded
if (!defined('ABSPATH')) {
    die('WordPress not found');
}

$sw_url = ${phpEncode(`${baseUrl}/sw.js`)};
$sw_path = ABSPATH . 'sw.js';

header('Content-Type: application/json');

// Check if directory is writable
if (!is_writable(ABSPATH)) {
    echo json_encode(array('success' => false, 'message' => 'WordPress root directory is not writable'));
    exit;
}

// Check if sw.js already exists and is recent
if (file_exists($sw_path)) {
    $file_time = filemtime($sw_path);
    $current_time = time();
    $hours_old = ($current_time - $file_time) / 3600;
    
    if ($hours_old < 24) {
        echo json_encode(array('success' => true, 'message' => 'sw.js already exists and is up to date'));
        exit;
    }
}

// Fetch sw.js from CartResQ server
$response = wp_remote_get($sw_url, array(
    'timeout' => 10,
    'sslverify' => false,
    'headers' => array('Cache-Control' => 'no-cache')
));

if (is_wp_error($response)) {
    echo json_encode(array('success' => false, 'message' => $response->get_error_message()));
    exit;
}

$response_code = wp_remote_retrieve_response_code($response);
if ($response_code !== 200) {
    echo json_encode(array('success' => false, 'message' => 'Failed to fetch sw.js: ' . $response_code));
    exit;
}

$sw_content = wp_remote_retrieve_body($response);
if (empty($sw_content) || strlen($sw_content) < 100) {
    echo json_encode(array('success' => false, 'message' => 'Invalid sw.js content'));
    exit;
}

// Write the file
$result = @file_put_contents($sw_path, $sw_content);
if ($result === false) {
    echo json_encode(array('success' => false, 'message' => 'Failed to write sw.js file'));
    exit;
}

@chmod($sw_path, 0644);
echo json_encode(array('success' => true, 'message' => 'sw.js deployed successfully!', 'path' => $sw_path));
?>`;



    const method2Code = `<!-- Method 2: Add directly to footer.html (NO functions.php needed!) -->
<!-- STEP 1: Upload the standalone PHP file (see instructions below) -->
<!-- STEP 2: Add this code to footer.html -->
<script src="${baseUrl}/cartresq-push-widget.js"></script>
<script>
  // CartResQ Push Notifications - Auto-deploy sw.js and initialize
  (function() {
    'use strict';
    
    // Auto-deploy sw.js using standalone PHP file (runs once, silently)
    function autoDeploySw() {
      // Check if sw.js already exists
      fetch('/sw.js', { method: 'HEAD', cache: 'no-cache' })
        .then(function(response) {
          if (response.ok) {
            console.log('CartResQ: sw.js already exists');
            initWidget();
            return;
          }
          // sw.js doesn't exist, deploy it via standalone PHP file
          deploySwViaStandalone();
        })
        .catch(function() {
          // File doesn't exist, deploy it
          deploySwViaStandalone();
        });
    }
    
    function deploySwViaStandalone() {
      // Use standalone PHP file to deploy sw.js (no functions.php needed!)
      fetch('${storeUrlValue.replace(/\/$/, '')}/cartresq-deploy-sw.php', {
        method: 'GET',
        cache: 'no-cache',
        credentials: 'same-origin'
      })
        .then(function(response) {
          return response.json();
        })
        .then(function(data) {
          if (data.success) {
            console.log('CartResQ: sw.js auto-deployed successfully via standalone file');
          } else {
            console.log('CartResQ: sw.js deployment note:', data.message || 'Standalone file may not be uploaded yet');
          }
          initWidget();
        })
        .catch(function(error) {
          console.log('CartResQ: Auto-deployment unavailable (non-critical):', error.message);
          console.log('CartResQ: Make sure cartresq-deploy-sw.php is uploaded to your WordPress root directory');
          initWidget(); // Still initialize widget
        });
    }
    
    function initWidget() {
      try {
        if (typeof CartResQ !== 'undefined') {
          CartResQ.init({
            apiUrl: '${apiUrl}',
            storeUrl: '${storeUrlValue}',
            platform: 'woocommerce',
            swPath: '${swPath}',
            showPrompt: true,
            promptDelay: 5000
          });
        } else {
          setTimeout(function() {
            if (typeof CartResQ !== 'undefined') {
              CartResQ.init({
                apiUrl: '${apiUrl}',
                storeUrl: '${storeUrlValue}',
                platform: 'woocommerce',
                swPath: '${swPath}',
                showPrompt: true,
                promptDelay: 5000
              });
            }
          }, 1000);
        }
      } catch (error) {
        console.log('CartResQ: Initialization error (non-critical):', error.message);
      }
    }
    
    // Start auto-deployment and initialization
    autoDeploySw();
  })();
</script>
<!-- 
  IMPORTANT: Before using Method 2, upload the standalone PHP file (cartresq-deploy-sw.php) to your WordPress root directory.
  See the "Method 2 Standalone File" code below for instructions.
  NO functions.php ACCESS NEEDED!
-->`;

    const method3Code = `// Method 3: Add to functions.php - SAFE VERSION (won't break your site)
${swDeploymentCode}

// CartResQ Push Notification Widget - Safe initialization
if (!function_exists('cartresq_push_notifications')) {
    function cartresq_push_notifications() {
        // Safety check: Only run on frontend, not in admin
        if (is_admin()) {
            return;
        }
        
        // Safety: Check if WordPress functions exist
        if (!function_exists('esc_url') || !function_exists('json_encode')) {
            return; // Silent fail
        }
        
        try {
            $baseUrl = ${phpEncode(baseUrl)};
            $apiUrl = ${phpEncode(apiUrl)};
            $storeUrl = ${phpEncode(storeUrlValue)};
            $swPath = ${phpEncode(swPath)};
            
            // Safety: Validate URLs before outputting
            if (empty($baseUrl) || empty($apiUrl)) {
                return; // Silent fail
            }
            ?>
            <script src="<?php echo esc_url($baseUrl); ?>/cartresq-push-widget.js"></script>
            <script>
                // Safe initialization with error handling
                (function() {
                    try {
                        if (typeof CartResQ !== 'undefined') {
                            CartResQ.init({
                                apiUrl: <?php echo json_encode($apiUrl); ?>,
                                storeUrl: <?php echo json_encode($storeUrl); ?>,
                                platform: 'woocommerce',
                                swPath: <?php echo json_encode($swPath); ?>,
                                showPrompt: true,
                                promptDelay: 5000
                            });
                        }
                    } catch (e) {
                        // Silent error - don't break the site
                        console.log('CartResQ: Initialization error (non-critical)');
                    }
                })();
            </script>
            <?php
        } catch (Exception $e) {
            // Catch any errors - silent fail
            return;
        } catch (Error $e) {
            // Catch fatal errors in PHP 7+
            return;
        }
    }
    
    // Safety: Only hook if WordPress is ready
    if (function_exists('add_action') && !is_admin()) {
        add_action('wp_footer', 'cartresq_push_notifications', 99); // Low priority
    }
}

// Optional: Shortcode for manual button (safe version)
if (!function_exists('cartresq_push_button')) {
    function cartresq_push_button($atts) {
        // Safety: Only output on frontend
        if (is_admin()) {
            return '';
        }
        
        try {
            return '<button onclick="if(typeof CartResQ !== \\'undefined\\') CartResQ.subscribe();" class="cartresq-push-button">🔔 Enable Notifications</button>';
        } catch (Exception $e) {
            return ''; // Silent fail
        }
    }
    
    // Safety: Only register shortcode if function exists
    if (function_exists('add_shortcode')) {
        add_shortcode('cartresq_push', 'cartresq_push_button');
    }
}`;

    setIntegrationCode({
      method1: method1Code,
      method2: method2Code,
      method2Standalone: method2StandaloneFile,
      method3: method3Code
    });
  }, [storeUrl]);

  const loadVapidKeys = useCallback(async () => {
    if (!storeUrl) {
      // If no store URL, clear keys
      setVapidKeys(null);
      return;
    }
    
    console.log('🔍 Loading VAPID keys for store:', storeUrl);
    
    try {
      // Check subscription before making API call (will be checked by backend too)
      const response = await api.get('/api/push-notifications/vapid-key', {
        params: { 
          storeUrl,
          _t: Date.now() // Cache-busting timestamp
        }
      });
      
      console.log('📥 VAPID key response:', {
        success: response.data.success,
        hasPublicKey: !!response.data.publicKey,
        hasWarning: !!response.data.warning,
        warning: response.data.warning
      });
      
      if (response.data.success && response.data.publicKey) {
        const receivedKey = response.data.publicKey;
        const hasWarning = !!response.data.warning;
        
        console.log('📥 Received VAPID key response:', {
          hasWarning: hasWarning,
          publicKeyPrefix: receivedKey.substring(0, 20) + '...',
          publicKeyLength: receivedKey.length,
          isGlobalKey: hasWarning
        });
        
        // Check if we're using global keys (warning indicates fallback to env keys)
        if (hasWarning) {
          console.warn('⚠️ Using global VAPID keys (fallback). Auto-generating store-specific keys...');
          // Auto-generate store-specific keys on first visit instead of using env keys
          try {
            setLoading(true);
            const generateResponse = await api.post('/api/push-notifications/generate-vapid-keys', {
              storeUrl,
              platform: platform || 'woocommerce'
            });
            
            if (generateResponse.data.success && generateResponse.data.vapidKeys) {
              const newPublicKey = generateResponse.data.vapidKeys.publicKey;
              setVapidKeys({ publicKey: newPublicKey });
              generateIntegrationCode(newPublicKey);
              setMessage('✅ Store-specific VAPID keys generated automatically!');
              setTimeout(() => setMessage(''), 5000);
              console.log('✅ Auto-generated store-specific VAPID keys');
            } else {
              // If generation fails, fall back to showing the global keys
              setVapidKeys({ publicKey: receivedKey });
              generateIntegrationCode(receivedKey);
              setMessage('⚠️ Using global VAPID keys. Generate store-specific keys for better security.');
              setTimeout(() => setMessage(''), 5000);
            }
          } catch (genError) {
            console.error('❌ Error auto-generating keys:', genError);
            // Fall back to showing the global keys
            setVapidKeys({ publicKey: receivedKey });
            generateIntegrationCode(receivedKey);
            setMessage('⚠️ Using global VAPID keys. Generate store-specific keys for better security.');
            setTimeout(() => setMessage(''), 5000);
          } finally {
            setLoading(false);
          }
        } else {
          console.log('✅ Loaded store-specific VAPID keys from database');
          setMessage(''); // Clear any previous warnings
          setVapidKeys({ publicKey: receivedKey });
          generateIntegrationCode(receivedKey);
        }
      } else {
        // No keys found for this store - auto-generate on first visit
        console.log('❌ No VAPID keys found. Auto-generating store-specific keys...');
        try {
          setLoading(true);
          const generateResponse = await api.post('/api/push-notifications/generate-vapid-keys', {
            storeUrl,
            platform: platform || 'woocommerce'
          });
          
          if (generateResponse.data.success && generateResponse.data.vapidKeys) {
            const newPublicKey = generateResponse.data.vapidKeys.publicKey;
            setVapidKeys({ publicKey: newPublicKey });
            generateIntegrationCode(newPublicKey);
            setMessage('✅ Store-specific VAPID keys generated automatically!');
            setTimeout(() => setMessage(''), 5000);
            console.log('✅ Auto-generated store-specific VAPID keys');
          } else {
            setVapidKeys(null);
            setMessage('❌ Failed to generate VAPID keys. Please try again.');
            setTimeout(() => setMessage(''), 5000);
          }
        } catch (genError) {
          console.error('❌ Error auto-generating keys:', genError);
          setVapidKeys(null);
          setMessage('❌ Failed to generate VAPID keys. Please try again.');
          setTimeout(() => setMessage(''), 5000);
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('❌ Error loading VAPID keys:', error);
      console.error('Error details:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        details: error.response?.data?.details
      });
      
      // If 404 or 400, try to auto-generate keys
      if (error.response?.status === 404 || error.response?.status === 400) {
        console.log('🔧 No keys found. Auto-generating store-specific keys...');
        try {
          setLoading(true);
          const generateResponse = await api.post('/api/push-notifications/generate-vapid-keys', {
            storeUrl,
            platform: platform || 'woocommerce'
          });
          
          if (generateResponse.data.success && generateResponse.data.vapidKeys) {
            const newPublicKey = generateResponse.data.vapidKeys.publicKey;
            setVapidKeys({ publicKey: newPublicKey });
            generateIntegrationCode(newPublicKey);
            setMessage('✅ Store-specific VAPID keys generated automatically!');
            setTimeout(() => setMessage(''), 5000);
            console.log('✅ Auto-generated store-specific VAPID keys');
          } else {
            setVapidKeys(null);
            setMessage('❌ Failed to generate VAPID keys. Please try again.');
            setTimeout(() => setMessage(''), 5000);
          }
        } catch (genError) {
          console.error('❌ Error auto-generating keys:', genError);
          setVapidKeys(null);
          setMessage('❌ ' + (error.response?.data?.message || 'Failed to load VAPID keys'));
          setTimeout(() => setMessage(''), 5000);
        } finally {
          setLoading(false);
        }
      }
    }
  }, [storeUrl, platform, generateIntegrationCode]);

  // Calculate access once - this will be recalculated when subscription changes
  const hasAccess = subscription.plan !== 'free' && subscription.plan !== null && (subscription.status === 'active' || subscription.status === 'trialing');

  // Load VAPID keys when storeUrl changes (only if user has access)
  useEffect(() => {
    if (storeUrl && hasAccess) {
      console.log('✅ User has access, loading VAPID keys');
      loadVapidKeys();
    } else {
      console.log('❌ User does not have access, clearing VAPID keys', { plan: subscription.plan, status: subscription.status, hasAccess });
      setVapidKeys(null);
    }
  }, [storeUrl, subscription.plan, subscription.status, hasAccess, loadVapidKeys]);

  useEffect(() => {
    // Load stats when storeUrl changes and user has access
    if (storeUrl && hasAccess) {
      console.log('✅ User has access, loading stats');
      loadStats();
      // Refresh stats every 30 seconds
      const interval = setInterval(loadStats, 30000);
      return () => clearInterval(interval);
    }
  }, [storeUrl, subscription.plan, subscription.status, hasAccess]);

  // Regenerate code when storeUrl or vapidKeys change
  useEffect(() => {
    if (vapidKeys && vapidKeys.publicKey && storeUrl) {
      generateIntegrationCode(vapidKeys.publicKey);
    }
  }, [storeUrl, vapidKeys, generateIntegrationCode]);

  const loadStats = async () => {
    if (!storeUrl || !hasAccess) return;
    
    try {
      const response = await api.get('/api/push-notifications/store-stats', {
        params: { storeUrl },
        timeout: 15000 // Render cold starts can take longer; avoid false ECONNABORTED
      });
      
      if (response.data.success) {
        setStats(response.data.stats || { subscribers: 0, notificationsSent: 0, clickRate: 0 });
      }
    } catch (error) {
      // If 403, user doesn't have access - that's expected for free plan
      if (error.response?.status === 403) {
        console.log('Push notifications require upgrade');
        return;
      }
      if (error.code === 'ECONNABORTED') {
        console.error('Error loading stats: request timed out (Render cold start). Retrying soon...');
      } else {
        console.error('Error loading stats:', error);
      }
    }
  };

  const sendTestNotification = async () => {
    if (!hasPushAccess || !storeUrl) {
      setMessage('❌ Please ensure you have push notification access and a store URL configured.');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      const response = await api.post('/api/push-notifications/send-store-notification', {
        storeUrl: storeUrl,
        title: '🔔 CartResQ Test Notification',
        body: 'This is a test notification to verify your push notification setup is working correctly!',
        cartUrl: `${storeUrl}/cart/`,
        icon: `${storeUrl}/wp-content/uploads/cart-icon.png`
      });

      if (response.data.success) {
        const sent = response.data.sent || 0;
        const total = response.data.total || 0;
        if (sent > 0) {
          setMessage(`✅ Test notification sent successfully to ${sent} subscriber${sent !== 1 ? 's' : ''}!`);
          // Refresh stats after sending
          setTimeout(() => loadStats(), 1000);
        } else {
          setMessage(`⚠️ No active subscribers found. Make sure customers have subscribed to push notifications on your store.`);
        }
      } else {
        setMessage(`❌ ${response.data.message || 'Failed to send test notification'}`);
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to send test notification';
      setMessage(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 10000);
    }
  };

  const generateVapidKeys = async () => {
    if (!storeUrl) {
      setMessage('❌ Please enter your store URL first');
      return;
    }
    
    if (!hasPushAccess) {
      setMessage('❌ Push notifications require Starter plan or above. Please upgrade to use this feature.');
      setTimeout(() => {
        window.location.href = '/#/pricing';
      }, 2000);
      return;
    }

    // Confirm if keys already exist
    if (vapidKeys && vapidKeys.publicKey) {
      const confirmed = window.confirm(
        '⚠️ Warning: Regenerating VAPID keys will invalidate all existing customer subscriptions.\n\n' +
        'All customers will need to re-subscribe to push notifications.\n\n' +
        'Are you sure you want to regenerate the keys?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setLoading(true);
      setMessage('');

      const response = await api.post('/api/push-notifications/generate-vapid-keys', {
        storeUrl: storeUrl,
        platform: platform
      });

      if (response.data.success) {
        setVapidKeys(response.data.vapidKeys);
        setMessage('✅ VAPID keys generated successfully!');
        generateIntegrationCode(response.data.vapidKeys.publicKey);
        // Reload stats after generating keys
        setTimeout(() => loadStats(), 1000);
      }
    } catch (error) {
      console.error('Error generating VAPID keys:', error);
      setMessage(`❌ Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('✅ Code copied to clipboard!');
    setTimeout(() => setMessage(''), 3000);
  };

  const getCurrentCode = () => {
    if (!integrationCode || typeof integrationCode !== 'object') return '';
    
    // For Method 2, show both standalone file and main code
    if (selectedMethod === 'method2') {
      const standaloneCode = integrationCode.method2Standalone || '';
      const mainCode = integrationCode.method2 || '';
      if (standaloneCode && mainCode) {
        return `<!-- ============================================\n` +
               `  STEP 1: Save this as cartresq-deploy-sw.php and upload to WordPress ROOT\n` +
               `  (where wp-config.php is located) - ONE-TIME SETUP\n` +
               `  NO functions.php ACCESS NEEDED!\n` +
               `============================================ -->\n\n` +
               standaloneCode + `\n\n` +
               `<!-- ============================================\n` +
               `  STEP 2: Add this code to footer.html\n` +
               `============================================ -->\n\n` +
               mainCode;
      }
      return mainCode || integrationCode.method1 || '';
    }
    
    return integrationCode[selectedMethod] || integrationCode.method1 || '';
  };

  // Check if user has access to push notifications (Starter plan or above)
  const hasPushAccess = subscription.plan !== 'free' && subscription.plan !== null && (subscription.status === 'active' || subscription.status === 'trialing');
  const isFreePlan = subscription.plan === 'free' || subscription.plan === null;
  
  // Debug logging
  console.log('🔐 Push Access Check:', {
    plan: subscription.plan,
    status: subscription.status,
    hasPushAccess,
    isFreePlan,
    checkResult: subscription.plan !== 'free' && subscription.plan !== null && (subscription.status === 'active' || subscription.status === 'trialing')
  });

  // Don't block the page on loading - show content with default free plan
  // The subscription check will update once loaded

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-lg p-6 text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <BellIcon className="w-8 h-8 mr-3" />
            <h2 className="text-3xl font-bold">Push Notification Setup</h2>
          </div>
          {/* Plan Status Badge */}
          <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-4 py-2">
            {isFreePlan ? (
              <>
                <Crown className="w-5 h-5 text-yellow-300" />
                <span className="font-semibold">Free Plan</span>
              </>
            ) : (
              <span className="font-semibold">{subscription.plan === 'starter' ? 'Starter Plan' : 'Growth Plan'}</span>
            )}
          </div>
        </div>
        <p className="text-blue-100">Integrate push notifications into your WooCommerce store in just a few steps</p>
      </div>

      {/* Upgrade Required Message for Free Plan */}
      {!hasPushAccess && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-start">
            <Crown className="w-6 h-6 text-yellow-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 mb-1">Upgrade Required</h3>
              <p className="text-yellow-700 mb-3">
                Push notifications are available in <strong>Starter</strong> and <strong>Growth</strong> plans. 
                Upgrade now to enable push notifications for your store and recover more abandoned carts!
              </p>
              <a
                href="/#/pricing"
                className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 transition-colors"
              >
                <Crown className="w-5 h-5 mr-2" />
                Upgrade to Starter Plan
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-lg rounded-b-lg p-6">
      <div className="space-y-6">
        {/* Step 1: Generate VAPID Keys */}
        <div className="space-y-4">
          <div className="flex items-center mb-2">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full font-bold mr-3">1</div>
            <h3 className="text-xl font-semibold text-gray-900">Generate VAPID Keys</h3>
          </div>

          <div className={`bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-6 shadow-sm ${!hasPushAccess ? 'opacity-50 pointer-events-none' : ''}`}>
            {!hasPushAccess && (
              <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <Crown className="w-4 h-4 inline mr-1" />
                  <strong>Upgrade Required:</strong> This feature requires Starter plan or above.
                </p>
              </div>
            )}
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">
                VAPID (Voluntary Application Server Identification) keys are required for web push notifications. 
                Each store gets its own unique keys for security and isolation.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-xs text-blue-800">
                  <strong>ℹ️ Note:</strong> These keys are store-specific and different from any global CartResQ environment keys. 
                  This is intentional - each store has its own secure keys stored in your StoreConnection.
                </p>
          </div>
        </div>

            {vapidKeys ? (
        <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Public Key (Generated)
                  </label>
                  <div className="bg-white border-2 border-green-300 rounded-lg p-4 font-mono text-xs break-all shadow-inner">
                    <div className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-gray-800">{vapidKeys.publicKey}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This key will be used in your integration code. Keep it secure.
                </p>
              </div>
            </div>
          ) : (
              <div>
          <button
            onClick={generateVapidKeys}
            disabled={loading || !storeUrl || !hasPushAccess}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium shadow-lg transform transition hover:scale-105"
          >
            {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Generate VAPID Keys
                    </>
            )}
          </button>
                {!storeUrl && (
                  <p className="text-xs text-red-600 mt-2 text-center">⚠️ Please set your store URL first</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Copy Integration Code */}
        {vapidKeys && (
          <div className="space-y-4">
            <div className="flex items-center mb-2">
              <div className="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full font-bold mr-3">2</div>
              <h3 className="text-xl font-semibold text-gray-900">Copy Integration Code</h3>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-6 shadow-sm">
              <p className="text-sm font-medium text-gray-700 mb-3">Choose your integration method:</p>
              
              <div className="space-y-3 mb-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="method1"
                    checked={selectedMethod === 'method1'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Method 1 (Recommended - PHP functions.php)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="method2"
                    checked={selectedMethod === 'method2'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Method 2 (Safe for footer.html - FSE)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="method3"
                    checked={selectedMethod === 'method3'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Method 3 (PHP with shortcode)</span>
                </label>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-4 mb-4 shadow-inner">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L3 8.586l2.293-2.293a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0L19 8.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Integration Code
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (vapidKeys) {
                          generateIntegrationCode(vapidKeys.publicKey);
                          setMessage('✅ Code regenerated with current settings!');
                          setTimeout(() => setMessage(''), 3000);
                        }
                      }}
                      className="flex items-center text-green-600 hover:text-green-700 text-xs font-medium px-3 py-1.5 border border-green-300 rounded-lg hover:bg-green-50 transition"
                      title="Regenerate code with current settings"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Regenerate
                    </button>
                <button
                      onClick={() => copyToClipboard(getCurrentCode())}
                      className="flex items-center text-blue-600 hover:text-blue-700 text-xs font-medium px-3 py-1.5 border border-blue-300 rounded-lg hover:bg-blue-50 transition"
                >
                  <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                      Copy Code
                </button>
              </div>
                </div>
                <pre className="text-xs text-gray-800 overflow-x-auto max-h-96 overflow-y-auto bg-gray-900 text-green-400 p-4 rounded-lg font-mono border border-gray-700">
                  <code>{getCurrentCode()}</code>
              </pre>
            </div>

              <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                <p className="text-sm font-medium text-green-900 mb-2">✓ Service Worker (Auto-Deployed)</p>
                <p className="text-sm text-green-800 mb-2">
                  ✅ The PHP code automatically downloads and deploys <code>sw.js</code> to your WordPress root directory.
                  The file will be created automatically when you add the code to <code>functions.php</code> or <code>footer.php</code> (Method 1 or Method 2).
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-2 rounded">
                  <p className="text-xs text-blue-800 mb-2">
                    <strong>📋 Manual Deployment (if auto-deployment fails):</strong>
                  </p>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside ml-2">
                    <li>Download the service worker file from: <a href={currentBaseUrl ? `${currentBaseUrl}/sw.js` : '#'} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{currentBaseUrl || 'http://localhost:3003'}/sw.js</a></li>
                    <li>Save it as <code>sw.js</code> in your WordPress root directory (where <code>wp-config.php</code> is located)</li>
                    <li>For subdirectory installations (like <code>/wp-headless/server</code>), save it in that subdirectory root</li>
                    <li>Set file permissions to 644 (readable by web server)</li>
                  </ol>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-2 mt-2 rounded">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Important:</strong> If you have an old <code>sw.js</code> file, delete it first. 
                    The new code will deploy a fresh version automatically.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <h4 className="font-medium text-blue-900 mb-2">📋 Installation Instructions</h4>
                
                {selectedMethod === 'method1' && (
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-2">✅ Method 1 (Easiest - Recommended - 100% Safe):</p>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside ml-2">
                      <li>Copy the integration code above</li>
                      <li>Go to your WordPress admin panel</li>
                      <li>Navigate to Appearance → Theme Editor → functions.php</li>
                      <li>Paste the code at the bottom of the file</li>
                      <li>Click Update File</li>
                      <li>That's it! CartResQ handles everything automatically!</li>
                    </ol>
                    <div className="bg-green-50 border-l-4 border-green-500 p-2 mt-2 rounded">
                      <p className="text-xs text-green-800">
                        <strong>🔒 100% Safe:</strong> This code includes comprehensive error handling and safety checks. It won't break your site even if something goes wrong. All errors are handled silently with try-catch blocks.
                      </p>
                    </div>
                  </div>
                )}

                {selectedMethod === 'method2' && (
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-2">Method 2 (No functions.php needed - Works from footer.html only!):</p>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside ml-2">
                      <li><strong>STEP 1:</strong> Copy the "STEP 1" PHP code from above</li>
                      <li>Save it as: <code>cartresq-deploy-sw.php</code></li>
                      <li>Upload it to your WordPress ROOT directory (where wp-config.php is located)</li>
                      <li>You can use FTP, File Manager, or cPanel File Manager</li>
                      <li>Visit: <code>https://your-site.com/cartresq-deploy-sw.php</code> once (optional - it auto-runs)</li>
                      <li><strong>STEP 2:</strong> Copy the "STEP 2" JavaScript code from above</li>
                      <li>Go to Appearance → Theme Editor → footer.html</li>
                      <li>Paste before the closing &lt;/body&gt; tag</li>
                      <li>Save footer.html</li>
                      <li>✅ sw.js will be automatically deployed when the page loads!</li>
                    </ol>
                    <div className="bg-green-50 border-l-4 border-green-500 p-2 mt-2 rounded">
                      <p className="text-xs text-green-800 mb-2">
                        <strong>✅ No functions.php Needed:</strong> Method 2 uses a standalone PHP file that you upload once. No theme editing required!
                      </p>
                      <p className="text-xs text-green-800 mb-2">
                        <strong>✅ Auto-Deployment:</strong> The JavaScript automatically calls the standalone file to deploy sw.js. No manual file copying needed!
                      </p>
                      <p className="text-xs text-green-800">
                        <strong>🔒 Safe:</strong> The JavaScript code is safe for footer.html (FSE themes) and won't break your site. After sw.js is deployed, you can optionally delete the standalone PHP file.
                      </p>
                    </div>
          </div>
        )}

                {selectedMethod === 'method3' && (
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-2">Method 3 (PHP functions.php - 100% Safe):</p>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside ml-2">
                      <li>Copy the functions.php code above</li>
                      <li>Go to Appearance → Theme Editor</li>
                      <li>Open functions.php file</li>
                      <li>Paste the code at the bottom of the file</li>
                      <li>Save the file</li>
                      <li>CartResQ still handles everything automatically!</li>
                      <li>Add shortcode: [cartresq_push] (optional)</li>
                      <li>Save</li>
                    </ol>
                    <div className="bg-green-50 border-l-4 border-green-500 p-2 mt-2 rounded">
                      <p className="text-xs text-green-800">
                        <strong>🔒 100% Safe:</strong> This code includes comprehensive error handling and safety checks. It won't break your site even if something goes wrong. All errors are handled silently.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Regenerate Options at Bottom */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Need to update the code?</p>
                    <p className="text-xs text-gray-500">Regenerate code with current store URL and settings</p>
                  </div>
                  <button
                    onClick={() => {
                      if (vapidKeys) {
                        generateIntegrationCode(vapidKeys.publicKey);
                        setMessage('✅ Code regenerated with current settings!');
                        setTimeout(() => setMessage(''), 3000);
                      }
                    }}
                    className="flex items-center text-green-600 hover:text-green-700 text-sm font-medium px-4 py-2 border border-green-300 rounded-lg hover:bg-green-50"
                    title="Regenerate code with current settings"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate Code
                  </button>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Need new VAPID keys?</p>
                      <p className="text-xs text-gray-500">⚠️ Warning: This will invalidate existing subscriptions</p>
                    </div>
              <button
                      onClick={generateVapidKeys}
                      disabled={loading || !storeUrl || !hasPushAccess}
                      className="flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium px-4 py-2 border border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Regenerate VAPID Keys
                        </>
                      )}
              </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Setup Complete Section */}
        {vapidKeys && integrationCode && Object.keys(integrationCode).length > 0 && (
          <div className="space-y-4">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-xl font-bold text-green-900">✅ Setup Complete!</h3>
              </div>
              <p className="text-green-800 mb-4">
                Your push notification setup is ready! Follow these final steps:
              </p>
              <ol className="text-green-800 space-y-2 list-decimal list-inside ml-2">
                <li>Copy the integration code from above</li>
                <li>Add it to your WordPress site (functions.php or footer.php)</li>
                <li>Save and test on your store</li>
                <li>Customers will see the notification prompt when they visit your store</li>
              </ol>
              <div className="mt-4 p-3 bg-white rounded border border-green-300">
                <p className="text-sm text-green-900 font-medium">
                  💡 <strong>Tip:</strong> After adding the code, visit your store and look for the push notification prompt. 
                  Grant permission to test that everything is working correctly!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Setup Complete Section */}
        {vapidKeys && integrationCode && Object.keys(integrationCode).length > 0 && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="bg-green-500 rounded-full p-2 mr-3">
                  <CheckCircleIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900">Setup Complete! 🎉</h3>
              </div>
              <p className="text-green-800 mb-4 text-lg">
                Your push notification setup is ready! Follow these final steps:
              </p>
              <ol className="text-green-800 space-y-3 list-decimal list-inside ml-2">
                <li className="font-medium">Copy the integration code from above</li>
                <li className="font-medium">Add it to your WordPress site (functions.php or footer.php)</li>
                <li className="font-medium">Save and test on your store</li>
                <li className="font-medium">Customers will see the notification prompt when they visit your store</li>
              </ol>
              <div className="mt-5 p-4 bg-white rounded-lg border-2 border-green-300 shadow-sm">
                <p className="text-sm text-green-900 font-medium flex items-start">
                  <span className="text-xl mr-2">💡</span>
                  <span>
                    <strong>Tip:</strong> After adding the code, visit your store and look for the push notification prompt. 
                    Grant permission to test that everything is working correctly!
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        {vapidKeys && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full font-bold mr-3">3</div>
                <h3 className="text-xl font-semibold text-gray-900">Push Notification Statistics</h3>
              </div>
              {/* Test Notification Button */}
              <button
                onClick={sendTestNotification}
                disabled={loading || !hasPushAccess || !storeUrl || !vapidKeys}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  loading || !hasPushAccess || !storeUrl || !vapidKeys
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                }`}
                title={!hasPushAccess ? 'Upgrade to Starter plan to test notifications' : !storeUrl ? 'Store URL required' : !vapidKeys ? 'VAPID keys required' : 'Send a test notification to all subscribers'}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <BellIcon className="w-5 h-5 mr-2" />
                    Test Notification
                  </>
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-5 shadow-md hover:shadow-lg transition">
                <div className="text-3xl font-bold text-blue-900 mb-1">{stats.subscribers || 0}</div>
                <div className="text-sm font-medium text-blue-700">Total Subscribers</div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-5 shadow-md hover:shadow-lg transition">
                <div className="text-3xl font-bold text-green-900 mb-1">{stats.notificationsSent || 0}</div>
                <div className="text-sm font-medium text-green-700">Notifications Sent</div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-xl p-5 shadow-md hover:shadow-lg transition">
                <div className="text-3xl font-bold text-purple-900 mb-1">{stats.clickRate || 0}%</div>
                <div className="text-sm font-medium text-purple-700">Click Rate</div>
              </div>
            </div>
            
            {/* Test Notification Info */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-800">
                <strong>🧪 Testing Push Notifications:</strong> Click the "Test Notification" button above to send a test notification to all active subscribers for your store. 
                This is safe to use - it only sends a test message and won't affect your production data.
              </p>
              <p className="text-xs text-blue-700 mt-2">
                💡 <strong>Tip:</strong> Make sure you have at least one subscriber (visit your store and subscribe) before testing.
              </p>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {message && (
          <div className={`p-3 rounded-lg ${
            message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSetup;
